import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { xor } from 'lodash'
import { useMemo } from 'react'

import { FieldSection } from '../../../formSection/fieldSection/FieldSection'
import { AddNewProgramFormModel } from '../../../AddNewProgram.types'
import { FieldSubSection } from '../../../formSection/fieldSubSection/FieldSubSection'

import { Checkbox, Flex, Grid, Typography } from '@percent/lemonade'
import { VettingEventScope } from '@percent/domain/verifications'

export function Compliance() {
  const { t } = useTranslation()

  const { setValue, control } = useFormContext<AddNewProgramFormModel>()

  const [flags, failingFlags] = useWatch({
    control,
    name: ['flags', 'failingFlags']
  })

  const onFlagChange = (flag: VettingEventScope) => {
    setValue('flags', xor(flags, [flag]))

    if (failingFlags?.includes(flag)) {
      setValue('failingFlags', xor(failingFlags, [flag]))
    }
  }

  const onFailingFlagChange = (flag: VettingEventScope) => setValue('failingFlags', xor(failingFlags, [flag]))

  const checkboxes = useMemo<{ label: string; value: VettingEventScope }[]>(
    () => [
      {
        label: t('form.sanction'),
        value: 'sanction'
      },
      {
        label: t('form.warning'),
        value: 'warning'
      },
      {
        label: t('form.adverseMedia'),
        value: 'adverseMedia'
      },
      {
        label: t('form.hateSpeech'),
        value: 'hateSpeech'
      },
      {
        label: t('form.controversial'),
        value: 'controversial'
      },
      {
        label: t('form.commercial'),
        value: 'commercial'
      }
    ],
    [t]
  )

  return (
    <FieldSection subSection>
      <Checkbox
        variant="default"
        active={!!(flags?.length || !!failingFlags?.length)}
        name="compliance"
        label={t('form.compliance')}
        onChange={event => {
          const isChecked = (event as React.ChangeEvent<HTMLInputElement>).target.checked

          if (!isChecked) {
            setValue('flags', [])
            setValue('failingFlags', [])
          } else {
            const defaultFlags: VettingEventScope[] = ['sanction', 'warning', 'adverseMedia', 'hateSpeech']
            setValue('flags', defaultFlags)
            setValue('failingFlags', defaultFlags)
          }
        }}
      />
      <FieldSubSection>
        <Grid>
          <Grid.Col span={4}>
            <Flex direction="column" gap={8}>
              <Typography variant="default">{t('form.flags')}</Typography>
              {checkboxes.map(({ label, value }) => (
                <Checkbox
                  key={`${value}-flag`}
                  variant="default"
                  active={flags?.includes(value)}
                  name={`${value}-flag`}
                  label={label}
                  dataTestId={`${label}-flag`}
                  onChange={() => onFlagChange(value)}
                />
              ))}
            </Flex>
          </Grid.Col>
          <Grid.Col span={4}>
            <Flex direction="column" gap={8}>
              <Typography variant="default">{t('form.failingFlags')}</Typography>
              {checkboxes.map(({ label, value }) => (
                <Checkbox
                  key={`${value}-failingFlag`}
                  variant="default"
                  active={failingFlags?.includes(value)}
                  name={`${value}-failingFlag`}
                  label={label}
                  dataTestId={`${label}-failingFlag`}
                  onChange={() => onFailingFlagChange(value)}
                  disabled={!flags?.includes(value)}
                />
              ))}
            </Flex>
          </Grid.Col>
        </Grid>
      </FieldSubSection>
    </FieldSection>
  )
}
